import React, { useEffect, useState, useMemo } from 'react';
import {
  Modal,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Button from '@/components/CustomButtons/Button';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import { modalStyle } from '@/views/Clients/Claim';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import CountryDropdown from '@/components/CountryDropdown';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';
import ManageFiles from './ManageFiles';
import axios from 'axios';
import {
  REFUND_TYPES,
  REFUND_STATUSES,
  REFUND_SUB_STATUS,
  TRANSACTION_STATUSES,
} from '@/utility';
import { useGetAllowedAttachmentTypes } from '@/api/hooks';
import { usePostHog } from 'posthog-js/react';

export const updateRefund = async ({
  oldInfo,
  newInfo,
  comment,
  resendCommunication,
  id,
  changed,
  attachments,
  disableMessage,
}) => {
  const formData = new FormData();

  if (oldInfo) formData.append('oldInfo', JSON.stringify(oldInfo));
  if (newInfo) formData.append('newInfo', JSON.stringify(newInfo));
  formData.append('comment', comment ?? '');
  formData.append('resendCommunication', resendCommunication ?? false);
  formData.append('id', id);
  formData.append('changed', changed ?? false);

  if (attachments?.internalAttachments?.deletedFiles?.length > 0) {
    formData.append(
      'deletedInternalFiles',
      JSON.stringify(attachments.internalAttachments.deletedFiles),
    );
  }
  if (attachments?.payeeAttachments?.deletedFiles?.length > 0) {
    formData.append(
      'deletedPayeeFiles',
      JSON.stringify(attachments.payeeAttachments.deletedFiles),
    );
  }

  if (newInfo?.memo) formData.append('memo', newInfo?.memo);
  if (newInfo?.business_payee_name)
    formData.append('business_payee_name', newInfo.business_payee_name);
  if (newInfo?.audience_id) formData.append('audience_id', newInfo.audience_id);

  attachments?.internalAttachments?.addFiles?.forEach((file) => {
    if (file.local) formData.append('internalFiles', file.file);
  });

  attachments?.payeeAttachments?.addFiles?.forEach((file) => {
    if (file.local) formData.append('payeeFiles', file.file);
  });

  return await axios.post(
    API_BASE_URL +
      `/update_refund_info/` +
      id +
      `?disableMessage=${disableMessage ?? false}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export default function UpdateRefundModal({
  currentInfo,
  originalInfo,
  modalErr,
  setModalErr,
  onInputChange,
  setCurrentInfo,
  setOriginalInfo,
  role,
  getRefunds,
  payments,
}) {
  const [attachments, setAttachments] = useState({
    internalAttachments: {
      addFiles: [],
      deletedFiles: [],
    },
    payeeAttachments: {
      addFiles: [],
      deletedFiles: [],
    },
  });
  const [audiences, setAudiences] = useState([]);
  const posthog = usePostHog();

  const showAudienceField =
    role === 'admin' || posthog?.isFeatureEnabled('new-audience-workflow');

  const isCustomPaymentTypesEnabled = posthog?.isFeatureEnabled(
    'new-custom-payment-types-workflow',
  );

  const { showAttachments } = useGetAllowedAttachmentTypes({
    refundType: currentInfo.refund_type,
    isCustomPaymentTypesEnabled,
    facilityId: currentInfo.facility_id,
  });

  useEffect(() => {
    const fetchAudiences = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/audiences/for-form${
            role === 'admin'
              ? '?organizationId=' + currentInfo.organization_id
              : ''
          }`,
        );
        setAudiences(response.data || []);
      } catch (e) {
        console.error('Failed to fetch audiences', e);
      }
    };
    if (showAudienceField) {
      fetchAudiences();
    }
  }, [showAudienceField]);

  const updateInfo = async (e, currentInfo, originalInfo) => {
    e.preventDefault();

    const newInfo = {
      first_name: currentInfo.first_name,
      last_name: currentInfo.last_name,
      email: currentInfo.email,
      refund_to_address_line1: currentInfo.refund_to_address_line1,
      refund_to_address_line2: currentInfo.refund_to_address_line2,
      refund_to_city: currentInfo.refund_to_city,
      refund_to_state: currentInfo.refund_to_state,
      refund_to_zip: currentInfo.refund_to_zip,
      refund_to_country: currentInfo.refund_to_country,
      memo: currentInfo.memo,
      business_payee_name: currentInfo.business_payee_name,
      audience_id: currentInfo.audience_id,
      client_bank_account_id: currentInfo.client_bank_account_id,
    };

    const oldInfo = originalInfo;

    //check if any info has changed
    let changed = false;
    for (let key in newInfo) {
      if (newInfo[key] != (oldInfo[key] + '')?.trim()) {
        changed = true;
        break;
      }
    }
    if (changed && !currentInfo.comment) {
      toast.error('Please enter a comment', { autoClose: 2000 });
      return;
    }

    try {
      await updateRefund({
        oldInfo,
        newInfo,
        comment: currentInfo.comment,
        resendCommunication: currentInfo.resendCommunication,
        id: currentInfo.id,
        changed,
        attachments,
        role,
      });
      setCurrentInfo(null);
      setOriginalInfo(null);

      getRefunds({ clear: true, limit: payments.length });
    } catch (err) {
      console.log(err);

      setModalErr(err.response?.data?.message);
    }
  };

  const handleResendCommunicationChange = (e) => {
    if (
      currentInfo?.refund_status === REFUND_STATUSES.UNCLAIMED ||
      currentInfo?.refund_status === REFUND_STATUSES.ESCHEATED ||
      currentInfo.transaction_status === TRANSACTION_STATUSES.ACH_RETURNED ||
      (currentInfo.refund_status === REFUND_STATUSES.EXCEPTION &&
        currentInfo.refund_sub_status === REFUND_SUB_STATUS.COMMUNICATION) ||
      currentInfo.transaction_status ===
        TRANSACTION_STATUSES.ACH_RETURN_AVAILABLE_TO_RECLAIM
    ) {
      onInputChange(e);
    } else {
      toast.error(
        'You can only Resend Communications if the refund is in Unclaimed, Escheated, ACH Returned Status, or ACH Return available to reclaim',
        { autoClose: 5000 },
      );
    }
  };

  const enableResendCommunication = useMemo(() => {
    return (
      currentInfo.refund_type === REFUND_TYPES.REGULAR ||
      currentInfo.refund_type === REFUND_TYPES.BUSINESS
    );
  }, [currentInfo?.refund_status]);

  return (
    <Modal
      open={currentInfo ? true : false}
      onClose={() => {
        setCurrentInfo(null);
        setOriginalInfo(null);
      }}>
      <form onSubmit={(e) => updateInfo(e, currentInfo, originalInfo)}>
        <Box
          sx={modalStyle}
          className='makeStyles-card-96'
          noValidate
          autoComplete='off'>
          <CardHeader style={{ transform: 'translateY(-50%)' }} color='primary'>
            <h4>Update Refund Info </h4>
          </CardHeader>
          <CardBody style={{ marginTop: '-20px' }}>
            {modalErr && (
              <p
                style={{
                  color: 'red',
                  padding: '0',
                  margin: 0,
                  fontSize: '15px',
                }}>
                {modalErr}
              </p>
            )}
            <GridContainer>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='email'
                  name='email'
                  labelText='Email'
                  value={currentInfo ? currentInfo['email'] : ''}
                  autoComplete='off'
                  onChange={onInputChange}
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  name='first_name'
                  labelText='First Name'
                  onChange={onInputChange}
                  value={currentInfo ? currentInfo['first_name'] : ''}
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  name='last_name'
                  labelText='Last Name'
                  onChange={onInputChange}
                  value={currentInfo ? currentInfo['last_name'] : ''}
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  name='refund_to_address_line1'
                  labelText='Address Line 1'
                  onChange={onInputChange}
                  value={
                    currentInfo ? currentInfo['refund_to_address_line1'] : ''
                  }
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  name='refund_to_address_line2'
                  labelText='Address Line 2'
                  onChange={onInputChange}
                  value={
                    currentInfo ? currentInfo['refund_to_address_line2'] : ''
                  }
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  name='refund_to_city'
                  labelText='City'
                  onChange={onInputChange}
                  value={currentInfo ? currentInfo['refund_to_city'] : ''}
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  name='refund_to_state'
                  labelText='State'
                  onChange={onInputChange}
                  value={currentInfo ? currentInfo['refund_to_state'] : ''}
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  name='refund_to_zip'
                  labelText='Zip Code'
                  onChange={onInputChange}
                  value={currentInfo ? currentInfo['refund_to_zip'] : ''}
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CountryDropdown
                  value={
                    currentInfo ? currentInfo['refund_to_country'] || '' : ''
                  }
                  onChange={(e) =>
                    onInputChange({
                      target: {
                        name: 'refund_to_country',
                        value: e.target.value,
                        type: 'text',
                      },
                    })
                  }
                />
              </GridItem>
              {showAudienceField && audiences.length > 0 && (
                <GridItem xs={12} sm={12} md={6}>
                  <div className='custom-select'>
                    <FormControl variant='standard'>
                      <InputLabel id='audience-label'>Audience</InputLabel>
                      <Select
                        labelId='audience-label'
                        value={
                          currentInfo
                            ? currentInfo['audience_id'] ||
                              currentInfo['audienceId'] ||
                              ''
                            : ''
                        }
                        onChange={(e) =>
                          onInputChange({
                            target: {
                              name: 'audience_id',
                              value: e.target.value,
                              type: 'text',
                            },
                          })
                        }
                        name='audience_id'
                        className='full-width-elem'>
                        {audiences.map((aud) => (
                          <MenuItem key={aud.id} value={aud.id}>
                            {aud.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </GridItem>
              )}

              {(isCustomPaymentTypesEnabled
                ? currentInfo.enable_payee_attachments ||
                  currentInfo.enable_internal_attachments
                : showAttachments) && (
                <GridItem xs={12} sm={12} md={12}>
                  <ManageFiles
                    refund={currentInfo}
                    onChange={setAttachments}
                    isInternalAttachmentsEnabled={
                      isCustomPaymentTypesEnabled
                        ? currentInfo?.enable_internal_attachments
                        : false
                    }
                    isPayeeAttachmentsEnabled={
                      isCustomPaymentTypesEnabled
                        ? currentInfo?.enable_payee_attachments
                        : true
                    }
                    isCustomPaymentTypesEnabled={isCustomPaymentTypesEnabled}
                  />
                </GridItem>
              )}
              {currentInfo.refund_type !== REFUND_TYPES.REGULAR && (
                <GridItem xs={12} sm={12} md={6}>
                  <CustomInput
                    type='text'
                    name='business_payee_name'
                    labelText='Business Payee Name'
                    onChange={onInputChange}
                    value={
                      currentInfo ? currentInfo['business_payee_name'] : ''
                    }
                    autoComplete='off'
                    formControlProps={{
                      fullWidth: true,
                    }}
                  />
                </GridItem>
              )}
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  name='memo'
                  labelText='Check Memo Field'
                  onChange={onInputChange}
                  value={currentInfo ? currentInfo['memo'] : ''}
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>

              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  type='text'
                  name='comment'
                  labelText='Comment (Required for Personal Data Changes)'
                  onChange={onInputChange}
                  value={currentInfo ? currentInfo['comment'] : ''}
                  autoComplete='off'
                  formControlProps={{
                    fullWidth: true,
                  }}
                />
              </GridItem>
            </GridContainer>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                }}>
                {enableResendCommunication && (
                  <>
                    {' '}
                    <input
                      type='checkbox'
                      name='resendCommunication'
                      onChange={handleResendCommunicationChange}
                      checked={!!currentInfo?.resendCommunication}
                    />
                    <label>Resend Communication</label>
                  </>
                )}
              </div>
              <Button variant='contained' color='rose' type='submit'>
                Update
              </Button>
            </div>
          </CardBody>
        </Box>
      </form>
    </Modal>
  );
}
